import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import axios from 'axios';
import ToastMsg from '../../../Components/ToastMsg/ToastMsg'
import { Colors } from '../../../Styles/Colors'
import { useHistory } from 'react-router-dom';
import { DataGrid, GridCsvExportMenuItem, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExportContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { CommonDeleteButton, CommonEditButton, DeletePopup } from '../../../Components/Common Components/CommonComponents';
import { CheckAccessFunc } from '../../../Components/CommonFunctions/CommonFunctions';
import { localGetItem } from '../../../Utility/Services/CacheProviderService';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import RestAPIService from '../../../Utility/Services/RestAPIService';

// status badge colours
const STATUS_COLOR = {
    uploaded: 'default',
    extracting: 'info',
    draft: 'warning',
    published: 'success',
    failed: 'error',
}

const LANGUAGE_LABELS = {
    en: 'English', hi: 'Hindi', ta: 'Tamil', te: 'Telugu', ml: 'Malayalam',
    kn: 'Kannada', bn: 'Bengali', gu: 'Gujarati', pa: 'Punjabi', or: 'Odia',
    mr: 'Marathi', ur: 'Urdu',
}

// Hide text labels on the default DataGrid toolbar buttons (icons only).
const ICON_ONLY_BTN_SX = {
    minWidth: 0,
    px: '0.4vw',
    fontSize: 0,
    '& .MuiButton-startIcon': { mx: 0 },
}

const ConsentToolbar = () => (
    <GridToolbarContainer className='eMed_refdoc_grid' sx={{ flexWrap: 'nowrap' }}>
        <Tooltip placement='top' title='Show/Hide Column' arrow>
            <GridToolbarColumnsButton sx={ICON_ONLY_BTN_SX} />
        </Tooltip>
        <Tooltip placement='top' title='Filters' arrow>
            <GridToolbarFilterButton componentsProps={{ button: { sx: ICON_ONLY_BTN_SX } }} />
        </Tooltip>
        <Tooltip placement='top' title='Table Density' arrow>
            <GridToolbarDensitySelector sx={ICON_ONLY_BTN_SX} />
        </Tooltip>
        <Tooltip placement='top' title='Export' arrow>
            <GridToolbarExportContainer sx={ICON_ONLY_BTN_SX}>
                <GridCsvExportMenuItem />
            </GridToolbarExportContainer>
        </Tooltip>
        <GridToolbarQuickFilter placeholder='Search' />
    </GridToolbarContainer>
)

export default function ConsentForm() {
    const history = useHistory();
    const [states, setStates] = useState({
        consentData: [],
        errorMsg: false,
        errorMsgText: '',
        successMsg: false,
        successMsgText: '',
        newPage: 0,
        pageSize: 10,
        isloader: false,
        isDelete: false,
        deleteTargetId: null,
        isUploadPop: false,
        selectedFile: null,
        fileError: '',
        previewUrl: '',
        uploadName: '',
        uploadCode: '',
        uploading: false,
        uploadProgress: 0,
        credits: null,
    })

    const commonDisable = CheckAccessFunc("front_office", "Settings", "Layout Configuration", "Consent Configuration", "SubTab")?.editAccess

    const errorMessage = (msg) => setStates(prev => ({ ...prev, errorMsg: true, errorMsgText: msg }))
    const successMessage = (msg) => setStates(prev => ({ ...prev, successMsg: true, successMsgText: msg }))

    const loadTemplates = async () => {
        try {
            setStates(prev => ({ ...prev, isloader: true }))
            const response = await RestAPIService.getAll(Serviceurls.CONSENT_TEMPLATE_LIST)
            const rows = (response?.data?.data || []).map((t, i) => ({ ...t, sno: i + 1 }))
            setStates(prev => ({ ...prev, consentData: rows, isloader: false }))
        } catch (e) {
            setStates(prev => ({ ...prev, isloader: false }))
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const loadCredits = async () => {
        try {
            const response = await RestAPIService.getAll(Serviceurls.CONSENT_FORM_CREDITS)
            setStates(prev => ({ ...prev, credits: response?.data?.data || null }))
        } catch (e) {
            // Non-fatal; balance chip just won't render.
        }
    }

    useEffect(() => { loadTemplates(); loadCredits() }, [])

    const handleClose = () => {
        setStates(prev => ({
            ...prev, isUploadPop: false, previewUrl: '', selectedFile: null,
            fileError: '', uploadName: '', uploadCode: '', uploading: false,
            uploadProgress: 0,
        }))
    }

    const handleFileChange = (file) => {
        if (!file) return;
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            setStates(prev => ({ ...prev, fileError: 'File size should not exceed 10MB', selectedFile: null, previewUrl: '' }));
            return;
        }
        const preview = URL.createObjectURL(file);
        setStates(prev => ({
            ...prev, selectedFile: file, fileError: '', previewUrl: preview,
            uploadName: prev.uploadName || file.name.replace(/\.[^.]+$/, '')
        }));
    };

    const handleUpload = async () => {
        if (!states.selectedFile) { setStates(prev => ({ ...prev, fileError: 'Please select a file' })); return; }
        if (!states.uploadName?.trim()) { setStates(prev => ({ ...prev, fileError: 'Please enter a form name' })); return; }
        if (!states.uploadCode?.trim()) { setStates(prev => ({ ...prev, fileError: 'Please enter a form code' })); return; }
        try {
            setStates(prev => ({ ...prev, uploading: true, uploadProgress: 0 }))
            const formData = new FormData();
            formData.append('file', states.selectedFile);
            formData.append('name', states.uploadName.trim());
            formData.append('code', states.uploadCode.trim());
            // Bust the project's process_request idempotency hash so re-uploading
            // the same name/code combo isn't blocked by the unique payload_hash.
            formData.append('_nonce', String(Date.now()))

            const token = localGetItem('AccessToken') || localGetItem('LoginAuthToken')
            const response = await axios.create({
                baseURL: RestAPIService.getBaseDomain(Serviceurls.CONSENT_TEMPLATE_UPLOAD),
                headers: {
                    'Authorization': 'Token ' + token,
                    'X-Client-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
                    'X-Screen-Path': window.location.pathname,
                },
            }).post('/' + Serviceurls.CONSENT_TEMPLATE_UPLOAD, formData, {
                onUploadProgress: (e) => {
                    if (!e?.total) return;
                    const pct = Math.min(99, Math.round((e.loaded / e.total) * 100))
                    setStates(prev => ({ ...prev, uploadProgress: pct }))
                },
            })
            setStates(prev => ({ ...prev, uploading: false, uploadProgress: 100 }))
            successMessage(response?.data?.message || 'Upload accepted; AI extraction started')
            handleClose()
            loadTemplates()
            loadCredits()
        } catch (e) {
            setStates(prev => ({ ...prev, uploading: false, uploadProgress: 0 }))
            errorMessage(e?.response?.data?.message || e.message)
            // Re-sync the balance — a 402 means another concurrent upload
            // raced ahead and drained the pool, and we want to update the chip.
            if (e?.response?.status === 402) loadCredits()
        }
    }

    const deleteConsent = async () => {
        try {
            await RestAPIService.delete(`${Serviceurls.CONSENT_TEMPLATE_DETAIL}${states.deleteTargetId}/`)
            successMessage('Template archived')
            setStates(prev => ({ ...prev, isDelete: false, deleteTargetId: null }))
            loadTemplates()
        } catch (e) {
            errorMessage(e?.response?.data?.message || e.message)
        }
    }

    const tableView = () => {
        const columns = [
            {
                field: 'sno', headerName: 'S.No', flex: 0.06, sortable: false, filterable: false,
                renderCell: (params) => params.row.sno
            },
            {
                field: 'code', headerName: 'Code', flex: 0.13,
                renderCell: (params) => params.row.code || '-'
            },
            {
                field: 'name', headerName: 'Consent Form Name', flex: 0.28,
                renderCell: (params) => (params.row.name?.length > 35 ?
                    <Tooltip placement="top" title={params.row.name}><Typography fontSize={'0.9vw'}>{params.row.name?.slice(0, 35) + '...'}</Typography></Tooltip> :
                    <Typography fontSize={'0.9vw'}>{params.row.name || '-'}</Typography>)
            },
            {
                field: 'language', headerName: 'Language', flex: 0.12,
                // valueGetter sets the column's underlying value to the full
                // language label (e.g. "English") so the CSV export uses it
                // instead of the raw 'en' code stored on the row.
                valueGetter: (params) => {
                    const code = params.row.language || 'en'
                    return LANGUAGE_LABELS[code] || code
                },
            },
            {
                field: 'status', headerName: 'Status', flex: 0.15,
                renderCell: (params) => (
                    <Chip
                        size="small"
                        label={params.row.status?.toUpperCase()}
                        color={STATUS_COLOR[params.row.status] || 'default'}
                        sx={{ fontSize: '0.75vw' }}
                    />
                )
            },
            ...(commonDisable ? [{
                field: 'action', headerName: 'Action', flex: 0.12, sortable: false,
                renderCell: (params) => (
                    <Stack direction="row" spacing={1}>
                        <CommonEditButton
                            size="1.5vw"
                            onClick={() => history.push(`/Settings/LayoutConfig/ConsentFormConfiguration/Add?id=${params.row.id}`)}
                        />
                        <CommonDeleteButton
                            size="1.5vw"
                            onClick={() => setStates(prev => ({ ...prev, isDelete: true, deleteTargetId: params.row.id }))}
                        />
                    </Stack>
                )
            }] : [])
        ]
        return (
            <Box sx={{ height: '70vh', marginTop: '.5vw' }}>
                <DataGrid
                    rows={states.consentData}
                    columns={columns}
                    getRowId={(row) => row.id}
                    page={states?.newPage}
                    pageSize={states?.pageSize}
                    components={{
                        Toolbar: ConsentToolbar,
                        NoRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                {'No Records To Be Shown'}
                            </Box>
                        )
                    }}
                    onPageChange={(newPage) => setStates(prev => ({ ...prev, newPage }))}
                    onPageSizeChange={(newPageSize) => setStates(prev => ({ ...prev, pageSize: newPageSize }))}
                    rowsPerPageOptions={[10, 20, 30]}
                    pagination
                    headerHeight={40}
                    density='compact'
                    disableSelectionOnClick
                    loading={states?.isloader}
                />
            </Box>
        )
    }

    const renderUploadPop = () => (
        <Dialog onClose={handleClose} open={states?.isUploadPop} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, fontSize: '1vw', fontWeight: 'bold' }}>Upload Consent Form</DialogTitle>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            <DialogContent dividers>
                <Stack direction="row" spacing={1} mb={1}>
                    <TextField size="small" fullWidth label="Form Name *"
                        value={states.uploadName}
                        onChange={(e) => setStates(prev => ({ ...prev, uploadName: e.target.value }))} />
                    <TextField size="small" sx={{ width: 140 }} label="Code *"
                        value={states.uploadCode}
                        onChange={(e) => setStates(prev => ({ ...prev, uploadCode: e.target.value }))} />
                </Stack>
                <Box
                    onDrop={(e) => { e.preventDefault(); handleFileChange(e.dataTransfer.files[0]) }}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => document.getElementById('consentFileInput').click()}
                    sx={{ height: '180px', border: '2px dashed #888', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#fafafa' }}
                >
                    <FileUploadIcon sx={{ fontSize: 40, color: '#888' }} />
                    <Typography variant="body1">Upload Consent Form Here</Typography>
                    <Typography variant="body2" color="textSecondary">Drag & Drop or Click to Select (PDF/JPG/PNG, Max 10MB)</Typography>
                    <Typography variant="body2" color="error" sx={{ mt: 0.5, fontWeight: 500 }}>
                        Only English consent forms are supported.
                    </Typography>
                    <input id="consentFileInput" type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e.target.files[0])} />
                </Box>
                {states.fileError && <Typography color="error" mt={1}>{states.fileError}</Typography>}
                {states.uploading && (
                    <Box mt={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="body2">Uploading...</Typography>
                            <Typography variant="body2" color="text.secondary">{states.uploadProgress}%</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={states.uploadProgress} />
                    </Box>
                )}
                {states.previewUrl && (
                    <Box mt={2}>
                        <Typography variant="body2">Preview:</Typography>
                        {states.selectedFile?.type === 'application/pdf'
                            ? <iframe src={states.previewUrl} width="100%" height="200px" title="PDF Preview" />
                            : <img src={states.previewUrl} alt="preview" style={{ width: '100%', maxHeight: '200px' }} />}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button size='small' variant='outlined' onClick={handleClose} sx={{ textTransform: 'capitalize' }}>Cancel</Button>
                <Button size='small' variant='contained' onClick={handleUpload}
                    disabled={states.uploading} sx={{ textTransform: 'capitalize' }}>
                    {states.uploading ? 'Uploading...' : 'Upload & Extract'}
                </Button>
            </DialogActions>
        </Dialog>
    )

    const msgClose = () => setStates(prev => ({ ...prev, errorMsg: false, errorMsgText: '', successMsg: false, successMsgText: '' }))
    const closeFilter = () => setStates(prev => ({ ...prev, isDelete: false, deleteTargetId: null }))

    const credits = states.credits
    const hasCredits = !!credits && credits.enabled && credits.is_active && credits.remaining_credits > 0
    const creditChipLabel = !credits || !credits.enabled
        ? 'AI Credits: feature not enabled'
        : !credits.is_active
            ? 'AI Credits: suspended'
            : `AI Credits: ${credits.used_credits} / ${credits.purchased_credits}`
    const creditChipColor = !credits || !credits.enabled || !credits.is_active
        ? 'default'
        : credits.remaining_credits === 0
            ? 'error'
            : credits.remaining_credits <= Math.max(5, Math.floor(credits.purchased_credits * 0.1))
                ? 'warning'
                : 'success'
    const uploadBlockedReason = !credits || !credits.enabled
        ? 'AI Consent Form is not enabled for this branch — contact support to purchase credits.'
        : !credits.is_active
            ? 'AI Consent Form has been suspended for this branch.'
            : credits.remaining_credits <= 0
                ? 'AI Consent Form credits exhausted — please top up to continue.'
                : ''

    return (
        <Box component={'div'} sx={{ height: '79.5vh', width: '94vw', backgroundColor: Colors.Background, padding: '.5vw' }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: '0.6vw' }}>
                {credits && (
                    <Tooltip placement='top' title={credits.renewal_date
                        ? `Renews on ${credits.renewal_date}${credits.renewal_cycle ? ` (${credits.renewal_cycle})` : ''}`
                        : 'Per-branch credit pool. 1 credit per AI extraction or re-extraction; failed extractions are refunded.'}>
                        <Chip size='small' label={creditChipLabel} color={creditChipColor} variant='outlined' />
                    </Tooltip>
                )}
                <Tooltip placement='top' title={uploadBlockedReason || ''} disableHoverListener={!uploadBlockedReason}>
                    <span>
                        <Button size='small' variant='contained' disabled={!commonDisable || !hasCredits}
                            sx={{ textTransform: 'capitalize', width: '12vw', mt: "0.5vw", mb: "0.5vw" }}
                            onClick={() => setStates(prev => ({ ...prev, isUploadPop: true }))}>
                            Upload Consent Form
                        </Button>
                    </span>
                </Tooltip>
            </Box>
            <Box sx={{ height: '70vh', width: '93vw', marginTop: '.5vw', backgroundColor: 'white' }}>
                {tableView()}
            </Box>
            {states?.errorMsg && <ToastMsg severity={'error'} msg={states?.errorMsgText} msgPop={msgClose} />}
            {states?.isUploadPop && renderUploadPop()}
            {states?.successMsg && <ToastMsg severity={'success'} msg={states?.successMsgText} msgPop={msgClose} />}
            {states?.isDelete &&
                <DeletePopup
                    DeletTitle={`Are you sure you want to archive this consent template?`}
                    DeleteNotify={"Issued forms remain available; the template will not appear in new-issue dropdowns."}
                    deleteAlertPopupClose={closeFilter}
                    removeData={deleteConsent}
                />}
        </Box>
    )
}
